<template>
  <div>
    <div class="map">
      <gmap-map
        :center="center"
        :zoom="zoom"
        ref="mapCartoGeneral"
        class="googleMap"
        :options="{
          streetViewControl: true,
          mapTypeControlOptions: mapType,
        }"
        @tilesloaded="initCarto">

        <div v-if="isLoadOnce">
          <!-- Marker du point de vente enregistré -->
          <gmap-marker
            v-if="pointDeVenteAvecMarker"
            :position="pointDeVenteAvecMarker.coordonnees"
            :icon="pointDeVenteAvecMarker.iconMarker">
          </gmap-marker>

          <!-- Liste des panneaux en cours de contrat -->
          <gmap-marker
            v-for="(marker, index) in oldPanneau"
            :key="`pno${index}`"
            :position="marker.adresse.coordonnees"
            :icon="marker.icon"
            @click="actionPanneau(marker, 'view')">
          </gmap-marker>

          <!-- Liste des nouveaux panneaux enregistré -->
          <gmap-marker
            v-for="(marker, index) in newPanneau"
            :key="`pnn${index}`"
            :position="marker.adresse.coordonnees"
            :icon="marker.icon"
            @click="actionPanneau(marker)">
          </gmap-marker>

          <!-- Liste des panneaux inactif -->
          <gmap-marker
            v-for="(marker, index) in panneauxInactifs"
            :key="`pni${index}`"
            :position="marker.adresse.coordonnees"
            :icon="marker.icon"
            @click="actionPanneau(marker, 'view')">
          </gmap-marker>
        </div>

        <!-- Markeur du panneau en cours de création -->
        <gmap-marker
          v-if="showMarkerChangePanneau && isLoadOnce"
          :position="markerPanneau.coordonnees"
          :icon="markerPanneau.icon"
          :draggable="true"
          @dragend="updateCoordonneesPanneau">
        </gmap-marker>

        <!-- Markeur du point de vente en cours de création -->
        <gmap-marker
          v-if="showMarkerChangePlacePdv && isLoadOnce"
          :position="pointDeVenteAdresse.coordonnees"
          :icon="getIconPdv()"
          :draggable="true"
          @dragend="updateCoordonneesPvd">
        </gmap-marker>

         <!-- Liste des concurrents -->
          <gmap-marker
            v-for="(marker, index) in listeMarkerPdvConcurrents.length ? listeMarkerPdvConcurrents : []"
            :key="`con${index}`"
            :position="marker.data.coordonnees"
            :icon="marker.iconMarker">
          </gmap-marker>
      </gmap-map>
    </div>
   </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { SET_POINT_DE_VENTE_ADRESSE, SET_MARKER_PANNEAU } from '@/store/modules/recommandations-mutation-type';
import {
  ZOOM_DEFAULT,
  CENTER_DEFAULT,
  STYLE_MAPS_POI_HIDDEN,
  MAP_TYPE_CONTROL_OPTIONS,
  MAP_TYPE_NAME,
} from '@/configuration/configurationCarte';
import {
  iconColorGetter,
  TAILLE_ICON_GRAND,
  getIconPdvData,
} from '@/services/gestionIcon/iconColorGetter';
import siteConfig from '@/configuration/siteConfig';
import { CATEGORIE_FORFAIT_AUDIT } from '@/configuration/configPanneau';
import { getCoordonneesInData } from '@/services/general/genererAdresse';

const Recommandations = createNamespacedHelpers('recommandations');
const PointDeVente = createNamespacedHelpers('pointDeVente');

export default {
  name:'app-map',
  data () {
    return {
      zoom: ZOOM_DEFAULT,
      center: CENTER_DEFAULT,
      mapType: MAP_TYPE_CONTROL_OPTIONS,
      panorama: null,
      isLoadOnce: false,
      pointDeVenteAvecMarker: null,
      listeMarkerPdvConcurrents: [],
    };
  },
  watch: {
    'pointDeVenteAdresse.adresse1'() {
      this.levelZoom();
    },
    'markerPanneau.adresse1'() {
      if (this.$route.name == 'creer-panneau') this.levelZoom();
    },
    'markerPanneau.streetView'() {
      this.streetViewMap();
    },
    activeTabListePanneau() {
      this.levelZoom();
    },
    panneauxInactifs() {
      this.levelZoom();
    },
    async pointDeVenteRecommandation() {
      await this.genererPdvAvecMarker();
      this.levelZoom();
    },
    async getPdvConcurrentsSelected() {
      await this.genererListePdvConcurrentAvecMarker();
      this.levelZoom();
    },
  },
  computed: {
    ...Recommandations.mapGetters({
      markerPanneau: 'markerPanneau',
      panneaux: 'panneaux',
      pointDeVenteAdresse: 'pointDeVenteAdresse',
      pointDeVenteRecommandation: 'pointDeVente',
      activeTabListePanneau: 'activeTabListePanneau',
    }),
    ...PointDeVente.mapGetters({
      getPdvConcurrentsSelected: 'pdvConcurrentSelected',
    }),
    showMarkerChangePanneau() {
      const isViewPdv = ['creer-panneau', 'modifier-panneau'].includes(this.$route.name);
      const coordonnees = getCoordonneesInData(this.markerPanneau);
      return isViewPdv && !!coordonnees;
    },
    showMarkerChangePlacePdv() {
      const isViewPdv = ['creer-point-de-vente', 'modifier-point-de-vente'].includes(this.$route.name);
      return isViewPdv && !!this.pointDeVenteAdresse;
    },
    newPanneau() {
      if (!this.panneaux.length || this.activeTabListePanneau !== 0) return [];
      return this.panneaux
        .filter(el => !el.estPanneauSousContrat)
        .filter(el => el.actif && el.categorie?.libelle !== CATEGORIE_FORFAIT_AUDIT);
    },
    oldPanneau() {
      if (!this.panneaux.length || this.activeTabListePanneau === 2) return [];
      const data = this.panneaux
        .filter(el => el.estPanneauSousContrat)
        .filter(el => el.actif && el.categorie?.libelle !== CATEGORIE_FORFAIT_AUDIT);
      data.forEach(pan => {
        const color = this.activeTabListePanneau === 0 ? '#4d4d4d' : (pan.categorie?.couleur || '#4d4d4d');
        pan.icon = iconColorGetter(color, TAILLE_ICON_GRAND, pan.azimut, true);
      });
      return data;
    },
    panneauxInactifs() {
      if (!this.panneaux.length || this.activeTabListePanneau !== 2) return [];
      const data = this.panneaux.filter(el => !el.actif);
      data.forEach(el => {
        if (el.estPanneauSousContrat) {
          el.icon = iconColorGetter('#4d4d4d', TAILLE_ICON_GRAND, el.azimut, true);
        }
      });
      return data;
    },
  },
  methods: {
    ...Recommandations.mapMutations({
      setMarkeurPanneau: SET_MARKER_PANNEAU,
      setPointDeVenteAdresse: SET_POINT_DE_VENTE_ADRESSE,
    }),
    async levelZoom() {
      if (!this.$refs.mapCartoGeneral.$mapPromise) return;
      await this.$refs.mapCartoGeneral.$mapPromise;
      await this.fitBounds();
    },
    async fitBounds() {
      const bounds = new google.maps.LatLngBounds();
      let isBound = false;
      const coordonneesList = [
        getCoordonneesInData(this.markerPanneau),
        getCoordonneesInData(this.pointDeVenteAdresse),
        getCoordonneesInData(this.pointDeVenteAvecMarker),
        ...this.newPanneau.map((el) => el.adresse.coordonnees),
        ...this.oldPanneau.map((el) => el.adresse.coordonnees),
        ...this.panneauxInactifs.map((el) => el.adresse.coordonnees),
        ...this.listeMarkerPdvConcurrents.map((el) => el.data.coordonnees),
      ].filter(Boolean);
      coordonneesList.forEach(el => {
        bounds.extend(el);
        isBound = true;
      });
      await this.$refs.mapCartoGeneral.fitBounds(bounds);
      if (!isBound) await this.resetCarto();
    },
    getIconPdv() {
      const srcUrl = require('@/assets/images/icon/marker_default_pdv.png');
      const height = 30;
      const width = 30;
      return {
        url: srcUrl,
        scaledSize: new google.maps.Size(width, height),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(width / 2, height),
      };
    },
    updateCoordonneesPanneau(val) {
      const coordonnees = {
        lat: val.latLng.lat(),
        lng: val.latLng.lng(),
      };
      this.setMarkeurPanneau({
        ...this.markerPanneau,
        coordonnees,
      });
    },
    updateCoordonneesPvd(val) {
      const coordonnees = {
        lat: val.latLng.lat(),
        lng: val.latLng.lng(),
      };
      this.setPointDeVenteAdresse({
        ...this.pointDeVenteAdresse,
        latitude: coordonnees.lat,
        longitude: coordonnees.lng,
        coordonnees,
      });
    },
    actionPanneau(panneau, action) {
      const { guid } = panneau;
      if (action === 'view') {
        this.$router.push({ name: 'view-panneau', params: { guid } });
      } else {
        this.$router.push({ name: 'modifier-panneau', params: { guid } });
      }
    },
    async resetCarto() {
      const map = await this.$refs.mapCartoGeneral.$mapPromise;
      map.setCenter(this.center);
      map.setZoom(this.zoom);
    },
    streetViewMap() {
      const coordonnees = getCoordonneesInData(this.markerPanneau);
      if (this.panorama && coordonnees) {
        this.panorama.setPosition(coordonnees);
        this.panorama.setVisible(this.markerPanneau.streetView);
      }
    },
    async initPoi() {
      const map = await this.$refs.mapCartoGeneral.$mapPromise;
      const styleMap = new google.maps.StyledMapType(
        STYLE_MAPS_POI_HIDDEN,
        { name: MAP_TYPE_NAME },
      );
      map.mapTypes.set("styled_map", styleMap);
    },
    async initStreetViewMap() {
      const map = await this.$refs.mapCartoGeneral.$mapPromise;
      this.panorama = map.getStreetView();
      this.panorama.setPov(({
        heading: 265,
        pitch: 0
      }));
      this.panorama.addListener('visible_changed',() => {
        this.setMarkeurPanneau({
          ...this.markerPanneau,
          streetView: this.panorama.getVisible(),
        });
      });
    },
    async genererPdvAvecMarker() {
      if (!this.pointDeVenteRecommandation) return;
      const { icon, srcImg } = this.pointDeVenteRecommandation;
      const resIcon = await getIconPdvData(icon || srcImg);
      if (this.pointDeVenteRecommandation.adressePostale.latitude
        && this.pointDeVenteRecommandation.adressePostale.longitude)
      this.pointDeVenteAvecMarker = {
        icon,
        srcImg,
        iconMarker: resIcon.icon,
        coordonnees: {
          lat: this.pointDeVenteRecommandation.adressePostale.latitude,
          lng: this.pointDeVenteRecommandation.adressePostale.longitude,
        }
      };
    },
    async initCarto() {
      if (this.isLoadOnce) return;
      try {
        await this.initPoi();
        await this.genererPdvAvecMarker();
        if (this.pointDeVenteAvecMarker || this.markerPanneau) {
          await this.levelZoom();
        }
        await this.initStreetViewMap();
      } finally {
        this.isLoadOnce = true;
      }
    },
    async genererListePdvConcurrentAvecMarker() {
      const { widthIcon, heightIcon } = (siteConfig.default).pdvConcurent;
      const liste = cloneDeep(this.getPdvConcurrentsSelected);
      const req = [];
      liste.forEach(el => {
        req.push(getIconPdvData(el.data.urlLogo, widthIcon, heightIcon, ''));
      });
      const res = await Promise.all(req);
      liste.forEach((el, i) => {
        el.iconMarker = res[i].icon;
      });
      this.listeMarkerPdvConcurrents = liste;
    },
  },
};
</script>
